import React, { useEffect, useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { adminCreateWork, adminUpdateWork, adminDeleteWork } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Save, X, Pencil, Loader2, Calendar, Building2, Film, Sparkles } from 'lucide-react';
import ImageUpload from './ImageUpload';
import { I18nInput, bulkTranslate } from './I18nInput';
import { pickI18n } from '../../i18n/helpers';

const empty = {
  order: 0,
  title: { es: '', en: '' },
  distributor: { es: '', en: '' },
  releaseDate: '',
  posterUrl: '',
};

const formatDate = (s) => {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return s; }
  }
  return s;
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{label}</span>
    {children}
  </label>
);

const AdminWorks = () => {
  const { works, reload } = useSite();
  const { toast } = useToast();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (editing === null) setForm(empty);
    else if (editing === 'new') setForm({ ...empty, order: (works?.length || 0) + 1 });
    else {
      const w = works.find((x) => x.id === editing);
      if (w) setForm(w);
    }
  }, [editing, works]);

  const save = async () => {
    if (!pickI18n(form.title, 'es') || !pickI18n(form.distributor, 'es')) {
      toast({ title: 'Faltan datos', description: 'Título y distribuidora (en ES) son obligatorios.' });
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') await adminCreateWork(form);
      else await adminUpdateWork(editing, form);
      await reload();
      toast({ title: 'Trabajo guardado' });
      setEditing(null);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este trabajo?')) return;
    await adminDeleteWork(id);
    await reload();
    toast({ title: 'Trabajo eliminado' });
  };

  const translateAll = async () => {
    setTranslating(true);
    try {
      await bulkTranslate([
        { value: form.title, set: (v) => setForm((f) => ({ ...f, title: v })) },
        { value: form.distributor, set: (v) => setForm((f) => ({ ...f, distributor: v })) },
      ], toast);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-3">Portfolio</div>
          <h1 className="font-display text-4xl text-white font-light">Trabajos</h1>
          <p className="text-white/50 mt-2 text-sm">Películas movilizadas. Cartel, título y distribuidora con versión ES/EN.</p>
        </div>
        {!editing && (
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm">
            <Plus className="w-4 h-4" /> Nuevo trabajo
          </button>
        )}
      </div>

      {editing ? (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="text-white text-lg">{editing === 'new' ? 'Nuevo trabajo' : 'Editar trabajo'}</h2>
            <div className="flex items-center gap-2">
              <button onClick={translateAll} disabled={translating} className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-[#00e0c7]/30 bg-[#00e0c7]/10 text-[#9be8dc] hover:bg-[#00e0c7]/20 text-xs font-medium transition-colors disabled:opacity-50">
                {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Traducir a EN
              </button>
              <button onClick={() => setEditing(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <ImageUpload
                label="Cartel de la película (2:3)"
                value={form.posterUrl}
                onChange={(url) => setForm({ ...form, posterUrl: url })}
                aspect="poster"
              />
            </div>

            <div className="lg:col-span-7 space-y-5">
              <I18nInput label="Título de la película *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <I18nInput label="Distribuidora *" value={form.distributor} onChange={(v) => setForm({ ...form, distributor: v })} />
              <Field label="Fecha de estreno">
                <input type="date" className="ldc-input" value={form.releaseDate} onChange={(e) => setForm({ ...form, releaseDate: e.target.value })} />
              </Field>
              <Field label="Orden de aparición">
                <input type="number" className="ldc-input" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
              </Field>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="px-5 h-11 rounded-full border border-white/10 text-white/70 text-sm hover:bg-white/5">Cancelar</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {(works || []).map((w) => {
            const title = pickI18n(w.title, 'es');
            const distributor = pickI18n(w.distributor, 'es');
            return (
              <div key={w.id} className="group rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden hover:border-[#00e0c7]/30 transition-colors">
                <div className="relative aspect-[2/3] bg-gradient-to-br from-[#0a1622] to-[#142838] overflow-hidden">
                  {w.posterUrl ? (
                    <img src={w.posterUrl} alt={title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/15">
                      <Film className="w-12 h-12" />
                    </div>
                  )}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/70 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => setEditing(w.id)} className="p-2 rounded-full bg-white/10 hover:bg-[#00e0c7]/30 text-white">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(w.id)} className="p-2 rounded-full bg-white/10 hover:bg-red-500/30 text-white">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-white text-sm font-medium leading-tight line-clamp-2 mb-2">{title}</h3>
                  <div className="flex items-center gap-1.5 text-[11px] text-white/50 mb-1">
                    <Building2 className="w-3 h-3" />
                    <span className="truncate">{distributor}</span>
                  </div>
                  {w.releaseDate && (
                    <div className="flex items-center gap-1.5 text-[11px] text-[#9be8dc]">
                      <Calendar className="w-3 h-3" />
                      {formatDate(w.releaseDate)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {(!works || works.length === 0) && (
            <div className="col-span-full text-white/40 p-10 text-center border border-dashed border-white/10 rounded-2xl">
              Aún no hay trabajos. Pulsa "Nuevo trabajo" para añadir el primero.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminWorks;
